import { zodResolver } from '@hookform/resolvers/zod'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Box from '@repo/ui/box'
import Button from '@repo/ui/button'
import { PlusIcon, XIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { forwardRef, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { getCseByCode } from '../../../../api/cse/get-cse-by-code'
import { canGoOutAloneOption } from '../../../../api/enrollments/can-go-out-alone-option'
import { useModalManager } from '../../../../lib/hooks/use-modal-manager'
import useTranslate from '../../../../lib/hooks/use-translate'
import { CreateEnrollmentInput } from '../../../../lib/types/models/enrollment/enrollment-input'
import { BaseFormProps } from '../../../../lib/types/utils'
import { getSubmitAction } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { FORM_MODE } from '../../../../utils/constants/form.constants'
import { Form } from '../../../elements/forms/form'
import ValidationButtons from '../../../elements/validation-buttons'
import EnrollmentPropositionFormModal from '../enrolment-create-proposition-steps/enrollment-proposition-form-modal'
import OtoIndividualPropositionFormModal from '../enrolment-create-proposition-steps/oto-individual-proposition-form-modal'
import OtoIndividualPropositionSummary from '../enrolment-create-proposition-steps/oto-individual-proposition-summary'
import { CREATE_ENROLLMENT_SCHEMA } from '../utils/enrollments-utils'
import EnrollmentInformationForm from './enrollment-information-form'
import EnrollmentPropositionDetails, {
  EnrollmentPropositionDetailsRef
} from './enrollment-proposition-details'

const CreateEnrollmentForm = forwardRef<
  EnrollmentPropositionDetailsRef,
  BaseFormProps<CreateEnrollmentInput, true>
>(({ defaultValues, ...validationButtonProps }, ref) => {
  const { t } = useTranslate()
  const form = useForm<CreateEnrollmentInput>({
    resolver: zodResolver(CREATE_ENROLLMENT_SCHEMA(t)),
    defaultValues,
    mode: FORM_MODE
  })

  // selectedData holds the tab index the OTO proposition modal opens on
  const {
    modalState,
    toggleModal,
    closeModel,
    selectedData: otoModalIndex
  } = useModalManager<'create-journey' | 'create-oto', number>(['create-journey', 'create-oto'])

  const { proposition, otoIndividualProposition, childBirthDate } = form.watch()

  const { data: canGoOutAloneOptionData } = useQuery({
    queryKey: ['enrollment-can-go-out-alone-option', proposition?.journey, childBirthDate],
    queryFn: () => {
      if (
        proposition &&
        childBirthDate &&
        proposition?.journey?.date.product.country.code &&
        proposition?.journey?.date.product.formula.id
      )
        return canGoOutAloneOption({
          childBirthDate,
          departureDate: proposition.journey.date.departureDate,
          returnDate: proposition.journey.date.returnDate,
          countryCode: proposition?.journey?.date.product.country.code,
          formulaId: proposition?.journey?.date.product.formula.id
        })
    },
    enabled:
      !!proposition &&
      !!childBirthDate &&
      !!proposition?.journey?.date.product.country.code &&
      !!proposition?.journey?.date.product.formula.id
  })

  // the badge names the CSE of the code entered in the stay selection
  const { data: cse } = useQuery({
    queryKey: ['cse', proposition?.cseCode, 'BASIC'],
    queryFn: () => getCseByCode(proposition!.cseCode!, 'BASIC'),
    enabled: !!proposition?.cseCode
  })

  //when form is submitted check if proposition is null and set a toast error message
  useEffect(() => {
    if (
      form.formState.submitCount > 0 &&
      !form.watch('proposition') &&
      !form.watch('otoIndividualProposition')
    )
      toast.error('Le séjour est obligatoire')
  }, [
    form.formState.submitCount,
    form.watch('proposition'),
    form.watch('otoIndividualProposition')
  ])

  const submit = (values: CreateEnrollmentInput) => {
    return getSubmitAction(validationButtonProps.buttonsConfig)(values)
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(submit)}>
        <Box className="p-5 flex flex-col gap-6">
          <InnerBox className="p-5 rounded-2xl">
            <div className="flex items-center gap-2">
              <Typography variant="text" color={tailwindColors.black.DEFAULT}>
                Séjour
              </Typography>
              <Typography.Link
                variant="label-semibold"
                color={tailwindColors.blue.DEFAULT}
                onClick={() => {
                  window.open('https://www.sejour-linguistique-lec.fr/', '_blank')
                }}
              >
                Accéder au site
              </Typography.Link>
              {/* the CSE code is entered in the stay selection, which keeps the stays of its selection */}
              {!otoIndividualProposition && !proposition?.cseCode && (
                <Button
                  type="button"
                  icon={PlusIcon}
                  variant="ghost"
                  label="Ajouter un code"
                  onClick={() => toggleModal('create-journey')}
                />
              )}
              {proposition?.cseCode && (
                <Button
                  type="button"
                  icon={() => <XIcon color={'red'} width={'20'} height={'20'} />}
                  iconToTheRight
                  className="border border-grey-light text-grey bg-white-hover hover:bg-white-light-hover"
                  variant="primary"
                  label={`CSE : ${cse?.label ?? proposition.cseCode}`}
                  onClick={() => form.setValue('proposition', { ...proposition, cseCode: null })}
                />
              )}
            </div>
            <div className="h-4" />
            {!proposition && !otoIndividualProposition && (
              <div className="flex gap-3 flex-row">
                <Button
                  type="button"
                  variant={form.formState.errors.proposition ? 'destructive' : 'secondary'}
                  icon={PlusIcon}
                  label="Ajouter un séjour"
                  className="w-fit"
                  onClick={() => toggleModal('create-journey')}
                />
                <Button
                  type="button"
                  variant={form.formState.errors.proposition ? 'destructive' : 'secondary'}
                  icon={PlusIcon}
                  label="Ajouter OTO Indiv"
                  className="w-fit"
                  onClick={() => toggleModal('create-oto', 0)}
                />
              </div>
            )}
            {otoIndividualProposition && (
              <OtoIndividualPropositionSummary
                input={otoIndividualProposition}
                childBirthDate={childBirthDate}
                onEdit={(tabIndex) => toggleModal('create-oto', tabIndex)}
                onInputChange={(values) => form.setValue('otoIndividualProposition', values)}
              />
            )}
            {proposition && (
              <EnrollmentPropositionDetails
                ref={ref}
                mode={'normal'}
                input={{ ...proposition, declaredFriends: form.watch('declaredFriends') }}
                country={form.watch('country')}
                onPropositionChange={(proposition) => {
                  form.setValue('proposition', proposition)
                  form.setValue('declaredFriends', proposition.declaredFriends)
                }}
              />
            )}
          </InnerBox>

          <EnrollmentInformationForm
            mode="create"
            cseCode={otoIndividualProposition ? null : proposition?.cseCode}
            canGoOutAloneOption={!!canGoOutAloneOptionData}
            departureDate={
              proposition?.journey?.date.departureDate ?? otoIndividualProposition?.departureDate
            }
          />
          <ValidationButtons {...validationButtonProps} />
        </Box>
        <EnrollmentPropositionFormModal
          isOpen={modalState['create-journey']}
          onClose={() => closeModel('create-journey')}
          defaultValues={{
            ...form.watch('proposition'),
            declaredFriends: form.watch('declaredFriends')
          }}
          handleSubmit={(values) => {
            form.setValue('proposition', values)
            form.setValue('declaredFriends', values.declaredFriends)
            closeModel('create-journey')
          }}
        />
        <OtoIndividualPropositionFormModal
          isOpen={modalState['create-oto']}
          defaultIndex={otoModalIndex ?? 0}
          defaultValues={form.watch('otoIndividualProposition')}
          childBirthDate={childBirthDate}
          onClose={() => closeModel('create-oto')}
          handleSubmit={(values) => {
            form.setValue('otoIndividualProposition', values)
            form.clearErrors('proposition')
            closeModel('create-oto')
          }}
        />
      </form>
    </Form>
  )
})

export default CreateEnrollmentForm
